import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { clx } from "../../../../helpers/components";
import { useEntryAnimation } from "../../../shared/FramerMotionAnimations";
import style from "./style.module.scss";
import ctaImage from "../../../../assets/images/direction-cta.jpg";

// Lien de prise de rendez-vous du bouton « Planifier un échange ».
const RESERVE_SLOT_URL = "https://cal.com/izichange-manager";

// Animation d'assemblage : le texte arrive de la gauche (en cascade),
// l'illustration de la droite → les éléments se rassemblent dans la carte.
const cardV = {
    initial: {},
    animate: { transition: { staggerChildren: 0.18, delayChildren: 0.15 } },
};
const contentV = {
    initial: {},
    animate: { transition: { staggerChildren: 0.12 } },
};
const fromLeft = {
    initial: { opacity: 0, x: -32, y: 8 },
    animate: {
        opacity: 1,
        x: 0,
        y: 0,
        transition: { duration: 0.55, ease: "easeOut" },
    },
};
const fromRight = {
    initial: { opacity: 0, x: 64, scale: 0.96 },
    animate: {
        opacity: 1,
        x: 0,
        scale: 1,
        transition: { duration: 0.7, ease: "easeOut" },
    },
};

/**
 * Section « Contactez la direction » (Figma node 10030:6997).
 * Carte : colonne gauche (label + barre, titre, paragraphe, bouton) +
 * illustration à droite. Animation d'entrée : les éléments se rassemblent.
 */
export function DirectionCta({ base = "partners" } = {}) {
    const { t: t0 } = useTranslation("translations");
    const t = (key, opts) =>
        t0(typeof key === "string" ? key.replace(/^partners\./, `${base}.`) : key, opts);

    const [ref, setRef] = React.useState(null);
    const { hasAnimated } = useEntryAnimation(ref);
    const [windowWidth, setWindowWidth] = React.useState(null);
    const isMobile = windowWidth === null ? false : windowWidth < 800;
    React.useEffect(() => {
        setWindowWidth(window.innerWidth);
    }, []);
    const show = hasAnimated || isMobile;

    const openReserve = () => {
        if (!RESERVE_SLOT_URL || RESERVE_SLOT_URL === "#") return;
        window.open(RESERVE_SLOT_URL, "_blank", "noopener,noreferrer");
    };

    return (
        <div className={clx("container", style.wrapper)}>
            <motion.div
                ref={setRef}
                className={style.card}
                variants={cardV}
                initial="initial"
                animate={show ? "animate" : "initial"}
            >
                <motion.div className={style.content} variants={contentV}>
                    <motion.div className={style.labelBlock} variants={fromLeft}>
                        <span className={style.label}>
                            {t("partners.direction-cta.label")}
                        </span>
                        <span className={style.labelBar} aria-hidden="true" />
                    </motion.div>

                    <motion.h2 className={style.title} variants={fromLeft}>
                        {t("partners.direction-cta.title")}
                    </motion.h2>

                    <motion.p className={style.subtitle} variants={fromLeft}>
                        {t("partners.direction-cta.subtitle")}
                    </motion.p>

                    <motion.button
                        type="button"
                        className={style.button}
                        onClick={openReserve}
                        aria-label={t("partners.direction-cta.btnTitle")}
                        variants={fromLeft}
                    >
                        <span>{t("partners.direction-cta.btnTitle")}</span>
                        <svg
                            className={style.arrow}
                            width="20"
                            height="12"
                            viewBox="0 0 20 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <g clipPath="url(#dirCtaArrow)">
                                <path
                                    d="M1.48159 5.10577C0.970212 5.10577 0.555664 5.52032 0.555664 6.0317C0.555664 6.54307 0.970212 6.95762 1.48159 6.95762V5.10577ZM13.7843 6.95762C14.2957 6.95762 14.7103 6.54307 14.7103 6.0317C14.7103 5.52032 14.2957 5.10577 13.7843 5.10577V6.95762ZM1.48159 6.95762H13.7843V5.10577H1.48159V6.95762ZM17.4894 6.27286L15.2003 8.03501L16.3299 9.50241L18.619 7.74028L17.4894 6.27286ZM14.7103 7.79384V4.26955H12.8584V7.79384H14.7103ZM15.2003 4.02838L17.4894 5.79052L18.619 4.32311L16.3299 2.56096L15.2003 4.02838ZM14.7103 4.26955C14.7103 4.01698 15.0001 3.87432 15.2003 4.02838L16.3299 2.56096C14.912 1.4695 12.8584 2.48024 12.8584 4.26955H14.7103ZM15.2003 8.03501C15.0001 8.18907 14.7103 8.04641 14.7103 7.79384H12.8584C12.8584 9.58315 14.912 10.5939 16.3299 9.50241L15.2003 8.03501ZM18.619 7.74028C19.7402 6.87715 19.7402 5.18624 18.619 4.32311L17.4894 5.79052C17.6476 5.91236 17.6476 6.15103 17.4894 6.27286L18.619 7.74028Z"
                                    fill="white"
                                />
                            </g>
                            <defs>
                                <clipPath id="dirCtaArrow">
                                    <rect width="20" height="12" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </motion.button>
                </motion.div>

                <motion.div className={style.media} variants={fromRight}>
                    <img src={ctaImage} alt="" className={style.image} />
                    {/* Bord gauche de l'image sculpté par la courbe (Figma) */}
                    <svg
                        className={style.mediaCurve}
                        viewBox="0 0 215 479"
                        fill="none"
                        preserveAspectRatio="xMinYMid meet"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path
                            d="M0 -1 L208 -1L17.5 191.5L15 197.5V202.5L16 208L21 213L23.5 215.5L26 218L27.5 220L34.5 227.5L51 251.5L57.5 264L61.5 273.5L64 282L66 288.5L68.5 298.5L70.5 313.5V326V337L61.5 381.5L54 399.5L49.5 409L46 416.5L41 425.5L32.5 440L28 446.5L24 452L19.5 458.5L15 465.5L7 475V476H15L15.9375 475 L0 479 Z"
                            fill="#ffffff"
                        />
                        <path
                            d="M208 -1L17.5 191.5L15 197.5V202.5L16 208L21 213L23.5 215.5L26 218L27.5 220L34.5 227.5L51 251.5L57.5 264L61.5 273.5L64 282L66 288.5L68.5 298.5L70.5 313.5V326V337L61.5 381.5L54 399.5L49.5 409L46 416.5L41 425.5L32.5 440L28 446.5L24 452L19.5 458.5L15 465.5L7 475V476H15L15.9375 475"
                            stroke="#F5F8FF"
                            strokeWidth="17"
                        />
                    </svg>
                </motion.div>
            </motion.div>
        </div>
    );
}
